import { useZero } from "@rocicorp/zero/react";
import { sharedMutators } from "../../shared/mutators";
import type { SpaceTab } from "../../shared/mutators/markSpaceTabViewed";
import { useViewVisit } from "../hooks/useViewVisit";
import { routes, useMakeUrl, useRouteMatch } from "../lib/urls";
import type { RouteNode } from "../lib/urls/types";
import { RightTabs, TabHeaderBar, Tabs } from "./TabHeader";
import { TabLink } from "./TabLink";

type Context =
  | { kind: "space"; spaceId: string }
  | { kind: "personal" }
  | { kind: "favorites" };

type Props = {
  context: Context;
  workflowsCount: number;
  linksCount: number;
};

function tabRoutes(
  context: Context,
  makeUrl: ReturnType<typeof useMakeUrl>
): {
  workflows: RouteNode;
  links: RouteNode;
  workflowsHref: string;
  linksHref: string;
  leaderboardHref?: string;
} {
  switch (context.kind) {
    case "space":
      return {
        workflows: routes.space,
        links: routes.space.links,
        workflowsHref: makeUrl(routes.space, { spaceId: context.spaceId }),
        linksHref: makeUrl(routes.space.links, { spaceId: context.spaceId }),
        leaderboardHref: makeUrl(routes.space.leaderboard, {
          spaceId: context.spaceId,
        }),
      };
    case "personal":
      return {
        workflows: routes.personal,
        links: routes.personal.links,
        workflowsHref: makeUrl(routes.personal),
        linksHref: makeUrl(routes.personal.links),
      };
    case "favorites":
      return {
        workflows: routes.favorites,
        links: routes.favorites.links,
        workflowsHref: makeUrl(routes.favorites),
        linksHref: makeUrl(routes.favorites.links),
      };
  }
}

export function PagesTabHeader({ context, workflowsCount, linksCount }: Props) {
  const makeUrl = useMakeUrl();
  const { links, workflowsHref, linksHref, leaderboardHref } = tabRoutes(
    context,
    makeUrl
  );
  const [isLinks] = useRouteMatch(links);
  const [isLeaderboard] = useRouteMatch(routes.space.leaderboard);
  const isWorkflows = !isLinks && !isLeaderboard;
  const z = useZero();
  const spaceId = context.kind === "space" ? context.spaceId : null;
  const spaceTab: SpaceTab = isLeaderboard
    ? "leaderboard"
    : isLinks
      ? "links"
      : "workflows";
  useViewVisit({
    visitKey: spaceId,
    tab: spaceId === null ? null : spaceTab,
    onView: ({ visitId, navSource, priorTab }) => {
      if (spaceId === null) return;
      z.mutate(
        sharedMutators.markSpaceTabViewed({
          spaceId,
          tab: spaceTab,
          visitId,
          navSource,
          priorTab: priorTab as SpaceTab | null,
        })
      );
    },
  });

  return (
    <TabHeaderBar>
      <Tabs>
        <TabLink
          to={workflowsHref}
          active={isWorkflows}
          icon="product-workflows"
          label="Workflows"
          count={workflowsCount > 0 ? workflowsCount : null}
        />
        <TabLink
          to={linksHref}
          active={isLinks}
          icon="product-cases"
          label="Links"
          count={linksCount > 0 ? linksCount : null}
        />
      </Tabs>
      {leaderboardHref && (
        <RightTabs>
          <TabLink
            to={leaderboardHref}
            active={isLeaderboard}
            icon="crown"
            label="Leaderboard"
          />
        </RightTabs>
      )}
    </TabHeaderBar>
  );
}
